import { useCallback, useEffect, useRef, useState } from 'react';

export type PlaySaveStatus =
  | 'saved'
  | 'pending'
  | 'saving'
  | 'error'
  | 'paused';

// HTTP writes outlive a component. Keep only their barriers, never a draft or
// authorization, across keyed Play/org remounts; release each settled scope.
const writesByScope = new Map<string, Promise<void>>();
export const pendingPlaySave = (scope: string) => writesByScope.get(scope);
/** The request was sent but its response did not confirm whether it committed. */
export class PlaySaveUnconfirmed extends Error {}

/** One canvas, one write at a time. Only a successful captured snapshot becomes
 * the baseline; edits made during that write remain pending and coalesce. */
export function usePlayAutosave({
  scope,
  value,
  baseline,
  enabled,
  automatic = true,
  delay = 700,
  write,
}: {
  scope: string;
  value: string;
  baseline: string;
  enabled: boolean;
  automatic?: boolean;
  delay?: number;
  /** May return the baseline the server accepted, when it corrected the
   *  snapshot (an On the server kept Off); else the snapshot is the baseline. */
  write: (snapshot: string, current: () => boolean) => Promise<string | void>;
}) {
  const [version, render] = useState(0);
  // Authority changes invalidate callbacks, not an HTTP write already sent.
  // Keep its serialization fence so restoring access cannot overlap that write.
  const queue = useRef({
    uncertain: new Set<string>(),
  });
  const initial = () => ({
    scope,
    mounted: false,
    value,
    baseline,
    seenBaseline: baseline,
    enabled,
    automatic,
    write,
    error: '',
    failedValue: '',
    inFlight: null as Promise<boolean> | null,
    explicit: false,
    force: false,
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
  });
  const state = useRef(initial());
  if (state.current.scope !== scope || state.current.enabled !== enabled)
    state.current = initial();
  const token = state.current;
  token.value = value;
  if (token.seenBaseline !== baseline) {
    token.baseline = baseline;
    token.seenBaseline = baseline;
  }
  token.enabled = enabled;
  token.automatic = automatic;
  token.write = write;
  const current = useCallback(
    () =>
      state.current === token &&
      token.mounted &&
      token.enabled &&
      token.scope === scope,
    [scope, token]
  );
  // Only a successful fresh read may resolve an uncertain or conflicted write.
  // This also works while writes are held, without enabling or sending a PUT.
  const acceptBaseline = useCallback(
    (snapshot: string) => {
      if (state.current !== token || !token.mounted) return;
      queue.current.uncertain.delete(scope);
      token.baseline = snapshot;
      token.seenBaseline = snapshot;
      token.error = '';
      token.failedValue = '';
      render((n) => n + 1);
    },
    [scope, token]
  );

  useEffect(() => {
    token.scope = scope;
    token.mounted = true;
    return () => {
      token.mounted = false;
      clearTimeout(token.timer);
    };
  }, [scope, token]);

  const flush = useCallback(
    async (
      explicit = true,
      snapshot?: string,
      force = false
    ): Promise<boolean> => {
      clearTimeout(token.timer);
      if (!current()) return false;
      if (snapshot !== undefined) token.value = snapshot;
      if (token.inFlight) return token.inFlight;
      token.force = force;
      if (
        !token.force &&
        token.value === token.baseline &&
        !queue.current.uncertain.has(scope) &&
        !writesByScope.has(scope)
      )
        return true;
      if (explicit) {
        token.explicit = true;
        token.error = '';
      }
      if (!explicit && (!token.automatic || token.failedValue === token.value))
        return false;
      const previous = writesByScope.get(scope) ?? Promise.resolve();
      const request = (async () => {
        // Assign inFlight before any synchronous validation can settle.
        await previous;
        try {
          while (current() && (token.automatic || token.explicit)) {
            if (
              !token.force &&
              token.value === token.baseline &&
              !queue.current.uncertain.has(scope)
            )
              return true;
            const snapshot = token.value;
            token.force = false;
            let accepted: string | void;
            try {
              accepted = await token.write(snapshot, current);
            } catch (error) {
              if (current()) {
                token.failedValue = snapshot;
                token.error =
                  error instanceof Error
                    ? error.message
                    : 'Could not save changes.';
              }
              if (!current() || error instanceof PlaySaveUnconfirmed)
                queue.current.uncertain.add(scope);
              return false;
            }
            if (!current()) {
              queue.current.uncertain.add(scope);
              return false;
            }
            queue.current.uncertain.delete(scope);
            token.baseline = accepted ?? snapshot;
            token.failedValue = '';
            token.error = '';
            // The server corrected this snapshot (an On it kept Off) and the
            // canvas takes its answer on the next render. Sending the same
            // snapshot again before that render would only be corrected again.
            if (token.value === snapshot && token.baseline !== snapshot)
              return true;
          }
          return false;
        } finally {
          token.inFlight = null;
          token.explicit = false;
        }
      })();
      token.inFlight = request;
      const barrier = request.then(() => {
        if (writesByScope.get(scope) === barrier) writesByScope.delete(scope);
        if (state.current.mounted) render((n) => n + 1);
      });
      writesByScope.set(scope, barrier);
      render((n) => n + 1);
      return request;
    },
    [current, token, scope]
  );

  useEffect(() => {
    clearTimeout(token.timer);
    if (
      !current() ||
      !automatic ||
      queue.current.uncertain.has(scope) ||
      (value === token.baseline &&
        !queue.current.uncertain.has(scope) &&
        !writesByScope.has(scope)) ||
      token.inFlight ||
      token.failedValue === value
    )
      return;
    token.error = '';
    token.timer = setTimeout(() => void flush(false), delay);
    return () => clearTimeout(token.timer);
  }, [
    scope,
    value,
    baseline,
    enabled,
    automatic,
    delay,
    current,
    flush,
    token,
    version,
  ]);

  const dirty = value !== token.baseline || queue.current.uncertain.has(scope);
  const error = token.failedValue === value ? token.error : '';
  const waiting = !!token.inFlight || (!!writesByScope.has(scope) && enabled);
  const status: PlaySaveStatus = waiting
    ? 'saving'
    : !dirty
      ? 'saved'
      : error
        ? 'error'
        : !enabled || !automatic
          ? 'paused'
          : 'pending';
  return {
    acceptBaseline,
    flush,
    status,
    error,
    uncertain: queue.current.uncertain.has(scope),
    failedSnapshot: token.failedValue,
    saving: waiting,
    dirty: dirty || waiting,
    current,
  };
}
